import { ChatMessageToast } from '../adminComponents/Toasts/Toasts';
import { PARTNER_CHAT } from '../routes/adminConstants';
import { useChatBubbleState } from '../store/chatBubble';
import { useChatSocketState } from '../store/useChatSocketState';

/**
 * One announcement of one message, however it was triggered.
 *
 * Two things raise these and they have to agree, or a message announces itself
 * twice: the socket, which is instant while the app is running, and the FCM
 * push, which is all there is once it is not.
 *
 * Three channels, because none of them is reliable on its own:
 *
 *   - the system notification, which is the only one that reaches a merchant
 *     looking at something else entirely - and whether it is ever drawn on
 *     screen is the operating system's call, not ours (macOS hides Chrome's
 *     after two seconds unless that app is set to Alerts);
 *   - a sound, which needs nobody to be looking at the screen at all. This is
 *     the one that works on a till in a kitchen;
 *   - an in-app toast, which is ours to guarantee and is a way INTO the
 *     conversation rather than a note about it.
 */

/** The same file the Orders and Deliveries sockets ring for a new order. */
const SOUND = '/sound3.mp3';

/** Everything from one conversation collapses into one notification. */
const tagFor = (conversationId: number | string | null | undefined) =>
  conversationId ? `conversation-${conversationId}` : 'conversation';

/**
 * Messages already announced, so the socket and the push cannot double up.
 *
 * The notification itself would collapse - same tag, and a replacement does
 * not re-alert - but a second sound and a second toast are two more things the
 * merchant has to dismiss for one message. Bounded because this outlives every
 * page in the app.
 */
const announced = new Set<string>();
const ANNOUNCED_LIMIT = 50;

const alreadyAnnounced = (messageId: number | string | undefined) => {
  if (messageId === undefined) return false;
  const key = String(messageId);
  if (announced.has(key)) return true;
  announced.add(key);
  if (announced.size > ANNOUNCED_LIMIT) {
    announced.delete(announced.values().next().value as string);
  }
  return false;
};

/**
 * Whether a message that just arrived should announce itself at all.
 *
 * The one case that stays quiet is the reader already looking at it: the
 * thread open AND the window in front. `observedConversationId` is set for as
 * long as a thread is mounted, so it covers the chat page and the merchant's
 * bubble panel alike.
 *
 * With an id, only THAT thread being open buys silence - which is what an
 * admin needs, since being tagged in one conversation while reading another is
 * exactly the case worth interrupting for. Without one, any open thread does:
 * the merchant observes a sentinel id that no message ever carries, so
 * comparing there would announce every message it was already showing.
 */
export const shouldAnnounceMessage = (
  conversationId?: number | string | null
) => {
  const observed = useChatSocketState.getState().observedConversationId;
  const readingThread =
    observed !== null &&
    (conversationId === undefined ||
      String(observed) === String(conversationId));
  return !(readingThread && document.hasFocus());
};

/**
 * Opens the conversation where the merchant already is, rather than
 * navigating: the support thread, or the group a message came from. The chat
 * page and the panel read the same tab and group, so whichever of the two is
 * showing follows.
 */
const openConversation = (groupId?: number | null) => {
  const chat = useChatBubbleState.getState();
  if (groupId) {
    chat.selectGroup(groupId);
  } else {
    chat.setTab('support');
  }
  if (window.location.pathname === PARTNER_CHAT) return;
  chat.setOpen(true);
};

const playSound = () => {
  try {
    const audio = new Audio(SOUND);
    // Blocked until the tab has seen a gesture; not worth reporting, and the
    // notification and the toast have already carried the message.
    audio.play().catch(() => undefined);
  } catch {
    /* No Audio in this environment. */
  }
};

const showNotification = async ({
  title,
  body,
  conversationId,
  groupId,
  data,
}: Options) => {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;
  if (!('serviceWorker' in navigator)) return;
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return;

  await registration.showNotification(title, {
    body,
    icon: '/pwa-192x192.png',
    /*
     * Outlives a banner on purpose. macOS hides a plain notification after a
     * couple of seconds - long enough to miss while looking at the till - and
     * this is what makes Chrome show it as an alert that waits to be dismissed
     * instead. The OS can still override it; hence the sound and the toast.
     */
    requireInteraction: true,
    tag: tagFor(conversationId),
    // What the service worker's notificationclick handler reads to know this
    // is a conversation push worth deep-linking - and, for a group, which
    // one: a push says so in its `content`, and this has none.
    data: {
      ...data,
      type: 'conversationMessage',
      conversationId,
      ...(groupId ? { conversationType: 'merchantGroup' } : {}),
    },
  });
};

type Options = {
  title: string;
  body: string;
  conversationId?: number | string | null;
  /** Deduplicates the socket against the push. */
  messageId?: number | string;
  /** Carried to the service worker's notificationclick handler. */
  data?: Record<string, unknown>;
  /**
   * Where the toast leads. Defaults to the merchant's support thread, or to
   * `groupId` when there is one; the admin app passes a route to the
   * conversation this belongs to.
   */
  onOpen?: () => void;
  /** Merchant side: the group the message is in, if it is not support. */
  groupId?: number | null;
  /**
   * The toast's heading, which is not the notification's title: the merchant's
   * toast says who it is from and there is only ever one answer, while a push
   * title is whatever the backend wrote. Left alone unless a caller has
   * something better - the admin's conversation name.
   */
  toastTitle?: string;
  /**
   * The thread whose being on screen makes this redundant - see
   * `shouldAnnounceMessage`. Omitted by the merchant, where any open thread is
   * the only thread.
   */
  quietWhenObserving?: number | string;
};

export const announceConversationMessage = async (options: Options) => {
  if (!shouldAnnounceMessage(options.quietWhenObserving)) return;
  if (alreadyAnnounced(options.messageId)) return;

  playSound();
  ChatMessageToast(
    options.body,
    options.onOpen ?? (() => openConversation(options.groupId)),
    options.toastTitle
  );
  await showNotification(options);
};
